/**
 * Pure gallery helpers shared by Appearance, analytics overlays, and hosts.
 * Keep free of React. Aligns with ImageManager spread shapes (array pairs + block items).
 */

const SPREAD_TYPE = 'spread';

/** Ids for one slideshow entry (single image or both sides of a spread). */
export function imageIdsInEntry(entry) {
    if (!entry) return [];
    if (Array.isArray(entry)) {
        return entry
            .map((side) => (typeof side === 'string' ? side : side?.id || side?.fileId))
            .filter(Boolean);
    }
    if (entry.type === SPREAD_TYPE) {
        const sides = entry.sides || entry.images || [];
        return sides
            .map((side) => (typeof side === 'string' ? side : side?.id || side?.fileId))
            .filter(Boolean);
    }
    const id = typeof entry === 'string' ? entry : entry?.id || entry?.fileId;
    return id ? [id] : [];
}

/**
 * The open .img may be one side of a spread. Return every image id on that slide
 * (left→right), or just the open id when it is a single.
 */
export function slideImageIdsFor(page, imageId) {
    if (!imageId) return [];
    const matchInList = (list) => {
        for (const entry of list || []) {
            const ids = imageIdsInEntry(entry);
            if (ids.includes(imageId)) return ids;
        }
        return null;
    };
    const fromGallery = matchInList(page?.gallery?.images);
    if (fromGallery?.length) return fromGallery;
    for (const block of page?.blocks || []) {
        if (!Array.isArray(block?.items)) continue;
        const fromBlock = matchInList(block.items);
        if (fromBlock?.length) return fromBlock;
    }
    return [imageId];
}

/**
 * Where the open image is shown: 'masonry', 'slideshow', 'directory', a stack
 * block's own layout, or 'other' (text, directory tile, unknown).
 */
export function imageHostLayout(page, imageId) {
    if (!imageId) return 'other';
    const inList = (list) => (list || []).some((entry) => imageIdsInEntry(entry).includes(imageId));
    const galleryType = page?.gallery?.type;
    if (galleryType === 'masonry' && inList(page.gallery.images)) return 'masonry';
    for (const block of page?.blocks || []) {
        if (Array.isArray(block?.items) && inList(block.items)) return block.layout || 'masonry';
    }
    if (galleryType === 'slideshow') return 'slideshow';
    return 'other';
}

/**
 * A file removed from the Bin for good, rebuilt from its tombstone so it can
 * be drawn as a placeholder.
 */
export function toDeletedImageFile(id, record = {}) {
    const description = String(record?.description || '').trim();
    const title = String(record?.title || '').trim();
    return {
        id,
        isDeleted: true,
        deletedAt: record?.deletedAt || null,
        originalname: record?.originalname || null,
        description: description || title || '',
        uploadDate: record?.createdAt || null,
        createdAt: record?.createdAt || null,
        dimensions: record?.dimensions || null,
        thumbnailUrl: record?.thumbnailUrl || null,
        url: record?.thumbnailUrl
            ? { original: String(record.thumbnailUrl).replace(/^\/+/, '') }
            : null,
    };
}

/** The file record, else its tombstone, else a marker for an id nothing knows. */
export function resolveFile(id, content) {
    if (!id) return null;
    const file = content?.files?.[id];
    if (file) return file;
    const tombstone = content?.deletedFiles?.[id];
    if (tombstone) return toDeletedImageFile(id, tombstone);
    return { id, isMissing: true };
}

/**
 * Files plus a placeholder record for every tombstone. Only admin and preview
 * content carries `deletedFiles`, so visitors keep dropping removed files.
 */
export function withDeletedFiles(content) {
    const tombstones = content?.deletedFiles;
    if (!tombstones || typeof tombstones !== 'object') return content;
    const files = content.files || {};
    const extra = {};
    Object.entries(tombstones).forEach(([id, record]) => {
        if (!files[id]) extra[id] = toDeletedImageFile(id, record);
    });
    if (!Object.keys(extra).length) return content;
    return { ...content, files: { ...extra, ...files } };
}

/**
 * Gallery rows are ids. The image editor needs the file records those ids point at.
 * With `keepDeleted`, ids whose file is gone stay in place as placeholders
 * (from `deletedFiles` when there is a tombstone), and spreads keep both sides.
 */
export function resolveGalleryImages(rawImages, files = {}, { keepDeleted = false, deletedFiles = null } = {}) {
    if (!Array.isArray(rawImages)) return [];
    const lookup = (imgId) => {
        if (!imgId) return null;
        if (files?.[imgId]) return files[imgId];
        if (!keepDeleted) return null;
        return resolveFile(imgId, { files, deletedFiles });
    };
    const withPlacement = (file, imgId, entry) => {
        if (entry && typeof entry === 'object') {
            return {
                ...file,
                id: imgId,
                scale: entry.scale,
                anchor: entry.anchor,
                fill: entry.fill,
                x: entry.x,
                y: entry.y,
            };
        }
        return { ...file, id: imgId };
    };
    return rawImages.map((item) => {
        if (Array.isArray(item) && item.length === 2) {
            const pair = item.map((spreadItem) => {
                const imgId = typeof spreadItem === 'string' ? spreadItem : spreadItem?.id;
                const file = lookup(imgId);
                return file ? withPlacement(file, imgId, spreadItem) : null;
            });
            return pair[0] && pair[1] ? pair : null;
        }
        const imgId = typeof item === 'string' ? item : item?.id;
        const file = lookup(imgId);
        return file ? withPlacement(file, imgId, item) : null;
    }).filter(Boolean);
}

export function galleryIds(images) {
    const ids = [];
    (images || []).forEach((item) => {
        if (Array.isArray(item)) {
            item.forEach((side) => {
                const id = typeof side === 'string' ? side : side?.id;
                if (id) ids.push(id);
            });
        } else if (typeof item === 'string') {
            ids.push(item);
        } else if (item?.id) {
            ids.push(item.id);
        }
    });
    return ids;
}

/**
 * The upload endpoint inserts each new file into the stored gallery. Add the
 * ones the draft does not hold yet at the drop position, so saving the draft
 * afterwards keeps them.
 */
export function adoptUploadedIds(draftImages, serverImages, dropPosition = 0) {
    const draft = Array.isArray(draftImages) ? draftImages : [];
    const known = new Set(galleryIds(draft));
    const added = galleryIds(serverImages).filter((id) => !known.has(id));
    if (!added.length) return draft;
    const at = Math.max(0, Math.min(draft.length, Number(dropPosition) || 0));
    return [...draft.slice(0, at), ...added, ...draft.slice(at)];
}

/** Collect every image id referenced by a page draft (gallery + blocks). */
export function collectPageImageIds(page) {
    const ids = new Set();
    const add = (entry) => {
        if (!entry) return;
        if (typeof entry === 'string') {
            ids.add(entry);
            return;
        }
        if (Array.isArray(entry)) {
            entry.forEach(add);
            return;
        }
        if (entry.id) ids.add(entry.id);
        if (entry.fileId) ids.add(entry.fileId);
        if (Array.isArray(entry.images)) entry.images.forEach(add);
        if (Array.isArray(entry.sides)) entry.sides.forEach(add);
    };
    (page?.gallery?.images || []).forEach(add);
    const walk = (blocks) => {
        (blocks || []).forEach((block) => {
            if (!block) return;
            if (block.type === 'gallery' || Array.isArray(block.items)) {
                (block.items || []).forEach(add);
            }
            if (Array.isArray(block.cells)) walk(block.cells);
        });
    };
    walk(page?.blocks);
    return ids;
}

/**
 * Remove an image id from a gallery images list.
 * Spreads that lose one side keep the remaining side as a single image.
 */
export function removeImageFromGalleryImages(images, imageId) {
    if (!imageId || !Array.isArray(images)) return images || [];
    const next = [];
    images.forEach((entry) => {
        const ids = imageIdsInEntry(entry);
        if (!ids.includes(imageId)) {
            next.push(entry);
            return;
        }
        if (ids.length <= 1) return;
        const remaining = ids.filter((id) => id !== imageId);
        if (remaining.length === 1) {
            if (Array.isArray(entry)) {
                const side = entry.find((item) => {
                    const id = typeof item === 'string' ? item : item?.id;
                    return id === remaining[0];
                });
                next.push(side ?? remaining[0]);
            } else if (entry?.type === SPREAD_TYPE) {
                const sides = entry.sides || entry.images || [];
                const side = sides.find((item) => {
                    const id = typeof item === 'string' ? item : item?.id || item?.fileId;
                    return id === remaining[0];
                });
                next.push(side ?? remaining[0]);
            } else {
                next.push(remaining[0]);
            }
        }
    });
    return next;
}

export default {
    imageIdsInEntry,
    slideImageIdsFor,
    resolveGalleryImages,
    resolveFile,
    toDeletedImageFile,
    withDeletedFiles,
    galleryIds,
    collectPageImageIds,
    removeImageFromGalleryImages,
};
